import {
    AuthContext,
    AvatarStack,
    ChatPanel,
    ComposePanel,
    CoreFetch,
    FilterMethod,
    IColumnFiltersConfig,
    IComment,
    ICustomFilter,
    IMessage,
    IRowAction,
    ITableColumn,
    ITableFetchOptions,
    ListProviderMessagesOrderEnum,
    MessageApi,
    MessageLinkEntityType,
    MessagingProvider,
    ProviderClaim,
    ProviderMessageSortType,
    StatusFilter,
    ToastContext,
    UserApi,
} from '@enter/core';
import { MessageListRedesigned } from '@enter/core';
import { mdiCheck, mdiClose, mdiContentCopy } from '@mdi/js';
import { CategoryTags, useDocumentView } from 'components';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext, useEffect, useState } from 'react';
import { Row } from 'react-table';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DialogContext } from 'services';
import { IInjectedRootStore, IMessageDetail, IMessageListingRequest } from 'stores';
import * as S from './messaging.styles';

interface IMatchParams {
    messageId: string;
}

type IMessagingRedesignedProps = IInjectedRootStore;

export const MessagingRedesigned: React.FunctionComponent<IMessagingRedesignedProps> = inject('rootStore')(
    observer((props: IMessagingRedesignedProps) => {
        const { rootStore } = props;
        if (!rootStore) {
            return <></>;
        }
        document.title = 'Provider Dashboard - Messaging';
        const navigate = useNavigate();
        const params = useParams();
        const location = useLocation();

        const authContext = useContext(AuthContext);
        const toastContext = useContext(ToastContext);
        const dialogContext = useContext(DialogContext);
        const [claims, setClaims] = useState<ProviderClaim[] | undefined>();
        const [isDetailLoading, setDetailLoading] = useState<boolean>();
        const [isListLoading, setListLoading] = useState<boolean>();
        const [fetchOptions, setFetchOptions] = useState<ITableFetchOptions>();
        const [messageId, setMessageId] = useState<string | undefined>();
        const [messageDetail, setMessageDetail] = useState<IMessageDetail | undefined>();
        const [openFile] = useDocumentView();

        const { roleId } = rootStore;
        const isReadOnly: boolean = roleId === 'PROVIDER_READ_ONLY';
        const isReadOnlyClaimChoice: boolean = false;

        useEffect(() => {
            return () => {
                authContext.cancelGroup && authContext.cancelGroup('messaging');
                authContext.cancelGroup && authContext.cancelGroup('messaging-metadata');
            };
        }, [authContext]);

        useEffect(() => {
            // Load metadata in a separate cancellation group to prevent interference
            const loadMetadata = async () => {
                try {
                    authContext.setGroup && authContext.setGroup('messaging-metadata');
                    await Promise.all([
                        rootStore.messageStore.loadMessageCategories(),
                        rootStore.messageStore.loadProviderUsers(),
                    ]);
                } catch (error) {
                    console.error('Failed to load messaging metadata:', error);
                }
            };
            loadMetadata();
        }, [rootStore.messageStore, authContext]);

        // Load message detail when URL changes
        useEffect(() => {
            const loadMessage = async () => {
                const paramsMessageId = params.messageId;
                if (paramsMessageId && paramsMessageId !== 'compose') {
                    setMessageId(paramsMessageId);
                    setMessageDetail(undefined); // Clear old message first
                    setDetailLoading(true);
                    try {
                        authContext.setGroup && authContext.setGroup('messaging');
                        authContext.cancelGroup && authContext.cancelGroup('messaging');
                        
                        const detail = await rootStore.messageStore.loadMessageDetail(paramsMessageId, true);
                        if (detail) {
                            rootStore.messageStore.markAsRead(paramsMessageId);
                            setMessageDetail(detail);
                        }
                    } finally {
                        setDetailLoading(false);
                    }
                } else {
                    setMessageId(undefined);
                    setMessageDetail(undefined);
                    setDetailLoading(false);
                }
            };
            loadMessage();
        }, [params.messageId, rootStore.messageStore, authContext]);

        const buildRequest = (showDone: boolean, newFetchData: ITableFetchOptions): IMessageListingRequest => {
            const { pageSize, pageIndex, sortBy, validFilters } = newFetchData;
            const sort: ProviderMessageSortType[] = [];
            const order: ListProviderMessagesOrderEnum[] = [];
            _.forEach(sortBy, rule => {
                let s;
                switch (rule.id) {
                    case 'Subject':
                        s = ProviderMessageSortType.SUBJECT;
                        break;
                    case 'Created':
                        s = ProviderMessageSortType.CREATED;
                        break;
                    case 'Updated':
                        s = ProviderMessageSortType.UPDATED;
                        break;
                    case 'Purpose':
                        s = ProviderMessageSortType.PURPOSE;
                        break;
                    case 'Priority':
                        s = ProviderMessageSortType.PRIORITY;
                        break;
                    default:
                        s = undefined;
                }
                if (s) {
                    sort.push(s);
                    order.push(
                        rule.desc ? ListProviderMessagesOrderEnum.DESCENDING : ListProviderMessagesOrderEnum.ASCENDING,
                    );
                }
            });
            const doneFilter: ICustomFilter = {
                filterId: 'filterOpen',
                filterMethod: FilterMethod.EQ,
                filterType: 'String',
                value: showDone ? '0' : '1',  // '1' = open/inbox, '0' = closed/done
            };
            return {
                page: pageIndex,
                pageSize,
                sort,
                order,
                filters: [...validFilters, doneFilter],
            };
        };

        const refresh = async (): Promise<void> => {
            if (fetchOptions) {
                // force refresh
                await handleFetchData();
            }
        };

        const handleFetchData = async (newFetchOptions?: ITableFetchOptions): Promise<void> => {
            const options = newFetchOptions || fetchOptions;
            if (options) {
                try {
                    setListLoading(true);
                    authContext.setGroup && authContext.setGroup('messaging');
                    authContext.cancelGroup && authContext.cancelGroup('messaging');
                    
                    const inboxReq = buildRequest(false, options);
                    const doneReq = buildRequest(true, options);
                    
                    const [inboxResult, doneResult] = await Promise.all([
                        rootStore.messageStore.loadMessageListing(inboxReq),
                        rootStore.messageStore.loadMessageListing(doneReq),
                    ]);
                    
                    setFetchOptions(options);
                    setListLoading(false);
                } catch (err) {
                    dialogContext.showError(err);
                    setListLoading(false);
                }
            }
        };

        const refreshMessageDetail = async (mid: string, forceFetch?: boolean): Promise<void> => {
            if (mid === 'compose') {
                return;
            }
            authContext.setGroup && authContext.setGroup('messaging');
            authContext.cancelGroup && authContext.cancelGroup('messaging');

            const detail = await rootStore.messageStore.loadMessageDetail(mid, forceFetch);
            if (detail && !_.isEqual(detail, messageDetail)) {
                setMessageDetail(detail);
                setMessageId(mid);
                setDetailLoading(false);
            }
        };

        const doSearch = async (term: string): Promise<ProviderClaim[]> => {
            setClaims([]);
            const results = await rootStore.claimStore.loadBasicClaimSearchResults(term);
            setClaims(results);
            return results;
        };

        const claimSearch = async (term: string): Promise<ProviderClaim[]> => {
            authContext.setGroup && authContext.setGroup('messaging-claim-search');
            authContext.cancelGroup && authContext.cancelGroup('messaging-claim-search');
            return await doSearch(term);
        };
        const composeComment = async ({ messageId: mid, body, files }: IComment): Promise<void> => {
            try {
                await rootStore.messageStore.addComment(rootStore.account, mid, body, files);
                await refreshMessageDetail(mid, true);
            } catch (err) {
                dialogContext.showError(err);
            }
        };
        const composeMessage = async ({ subject, body, claimId, files, links, priority, dueDate }: IMessage): Promise<void> => {
            try {
                // Extract claim IDs from links if available, otherwise use single claimId
                const claimIds = links?.claims?.map(c => c.id).filter(Boolean) || (claimId ? [claimId] : []);
                // Extract category IDs from links if available
                const categoryIds = links?.messageCategories?.map(c => c.id).filter(Boolean);
                // Extract user IDs from links if available
                const assignedUserIds = links?.users?.map(u => u.id).filter(Boolean);
                
                await rootStore.messageStore.composeMessage(rootStore.account, subject, body, claimIds, files, priority, dueDate, categoryIds, assignedUserIds);
                toastContext.notify?.('Message sent', 'left');
            } catch (err) {
                dialogContext.showError(err);
            }
        };
        const downloadDocument = async (id: string): Promise<void> => {
            const file = await rootStore.messageStore.embed(id);

            file.url && openFile(file.url);
        };
        const actualDownloadDocument = async (id: string): Promise<string> => {
            const file = await rootStore.messageStore.download(id);
            return file.url || '';
        };
        const markAsOpen = async (ids: string | string[]): Promise<void> => {
            try {
                const promises = _.map(
                    _.concat([], ids),
                    async (id): Promise<void> => rootStore.messageStore.markAsOpen(id),
                );
                await Promise.all(promises);
                await refresh();
                const count = Array.isArray(ids) ? ids.length : 1;
                toastContext.notify?.(`Successfully reopened ${count} message${count > 1 ? 's' : ''}`, 'left');
            } catch (err) {
                dialogContext.showError(err);
                throw err;
            }
        };
        const markAsClosed = async (ids: string | string[]): Promise<void> => {
            try {
                const promises = _.map(
                    _.concat([], ids),
                    async (id): Promise<void> => rootStore.messageStore.markAsClosed(id),
                );
                await Promise.all(promises);
                await refresh();
                const count = Array.isArray(ids) ? ids.length : 1;
                toastContext.notify?.(`Successfully marked ${count} message${count > 1 ? 's' : ''} as done`, 'left');
            } catch (err) {
                dialogContext.showError(err);
                throw err;
            }
        };
        const markAsRead = async (ids: string | string[]): Promise<void> => {
            const promises = _.map(
                _.concat([], ids),
                async (id): Promise<void> => rootStore.messageStore.markAsRead(id),
            );
            await Promise.all(promises);
        };
        const markAsUnread = async (ids: string | string[]): Promise<void> => {
            const promises = _.map(
                _.concat([], ids),
                async (id): Promise<void> => rootStore.messageStore.markAsUnread(id),
            );
            await Promise.all(promises);
        };
        const messageOpen = async (mid: string): Promise<void> => {
            const isCompose = location.pathname.endsWith('/compose');
            const paramsMessageId = params.messageId;
            
            if (isCompose) {
                navigate(`../${mid}`, { relative: 'path' });
            } else if (paramsMessageId) {
                navigate(`../${mid}`, { relative: 'path' });
            } else {
                navigate(mid);
            }
        };
        const messageClose = async (): Promise<void> => {
            navigate('..', { replace: true, relative: 'path' });
        };

        const showCompose = (): void => {
            const paramsMessageId = params.messageId;
            if (paramsMessageId) {
                navigate('../compose', { relative: 'path' });
            } else {
                navigate('compose');
            }
        };

        const renderMessageList = (): React.ReactElement | undefined => {
            const { doneTotal, doneMessages, inboxTotal, inboxMessages, unreadInboxTotal } = rootStore.messageStore;
            

            const mapMessages = (messages: typeof inboxMessages) => {
                return messages.map(m => {
                    const categoryNames = _.compact(
                        _.map(m.links?.messageCategories, cat => rootStore.messageStore.getCategoryName(cat.id || ''))
                    );
                    const assigneeNames = _.compact(
                        _.map(m.links?.users, user => rootStore.messageStore.getUserName(user.id || ''))
                    );
                    return {
                        ...m,
                        comments: m.commentCount,
                        documents: m.documentCount,
                        categories: categoryNames,
                        assignees: assigneeNames,
                    };
                });
            };

            const rowActions: IRowAction[] = [
                {
                    icon: mdiCheck,
                    tooltip: 'Mark as done',
                    action: async (row: Row<any>) => {
                        const messageId = row.original.id;
                        if (messageId) {
                            await markAsClosed(messageId);
                            toastContext.notify?.('Message marked as done', 'left');
                            await refresh();
                        }
                    },
                    isVisible: (row: Row<any>) => row.original.open === true,
                },
                {
                    icon: mdiClose,
                    tooltip: 'Mark as undone',
                    action: async (row: Row<any>) => {
                        const messageId = row.original.id;
                        if (messageId) {
                            await markAsOpen(messageId);
                            toastContext.notify?.('Message marked as undone', 'left');
                            await refresh();
                        }
                    },
                    isVisible: (row: Row<any>) => row.original.open === false,
                },
                {
                    icon: mdiContentCopy,
                    tooltip: 'Copy message ID',
                    action: (row: Row<any>) => {
                        const messageId = row.original.id;
                        if (messageId) {
                            navigator.clipboard.writeText(messageId);
                            toastContext.notify?.('Message ID copied to clipboard', 'left');
                        }
                    },
                },
            ];

            const customColumns: ITableColumn[] = [
                {
                    id: 'Categories',
                    Header: 'Categories',
                    accessor: 'categories',
                    Cell: ({ cell }: any): React.ReactElement => <CategoryTags categories={cell.value} />,
                    minWidth: 150,
                    maxWidth: 250,
                    disableSortBy: true,
                    show: true,
                },
                {
                    id: 'Assignee',
                    Header: 'Assignees',
                    accessor: 'assignees',
                    Cell: ({ cell }: any): React.ReactElement => <AvatarStack names={cell.value} />,
                    minWidth: 150,
                    maxWidth: 250,
                    disableSortBy: true,
                    show: true,
                },
            ];

            // Column-specific filters configuration
            const columnFiltersConfig: IColumnFiltersConfig = {
                From: {
                    filterType: 'String',
                    filterId: 'filterAuthor',
                    label: 'Filter by Author',
                    placeholder: 'Enter author name...',
                    defaultMethod: FilterMethod.LK,
                },
                Subject: {
                    filterType: 'String',
                    filterId: 'filterSubject',
                    label: 'Filter by Subject',
                    placeholder: 'Enter subject...',
                    defaultMethod: FilterMethod.LK,
                },
                Updated: {
                    filterType: 'DateTime',
                    filterId: 'filterUpdated',
                    label: 'Filter by Updated Date',
                    defaultMethod: FilterMethod.GTE,
                },
                Purpose: {
                    filterType: 'String',
                    filterId: 'filterPurpose',
                    label: 'Filter by Purpose',
                    placeholder: 'Enter purpose...',
                    defaultMethod: FilterMethod.LK,
                },
                Priority: {
                    filterType: 'Select',
                    filterId: 'filterPriority',
                    label: 'Filter by Priority',
                    defaultMethod: FilterMethod.EQ,
                    treatNullAsEmpty: true,
                    options: [
                        { label: 'Low', value: 'LOW' },
                        { label: 'Normal', value: 'NORMAL' },
                        { label: 'High', value: 'HIGH' },
                        { label: 'Urgent', value: 'URGENT' },
                        { label: 'None', value: null },
                    ],
                },
                Categories: {
                    filterType: 'AsyncSelect',
                    filterId: 'filterMessageCategoryId',
                    label: 'Filter by Categories',
                    defaultMethod: FilterMethod.EQ,
                    isMulti: true,
                    loadOptions: async (searchTerm: string) => {
                        const categories = rootStore.messageStore.messageCategories;
                        return categories
                            .filter(cat => !searchTerm || cat.name?.toLowerCase().includes(searchTerm.toLowerCase()))
                            .map(cat => ({
                                label: cat.name || '',
                                value: cat.id || '',
                            }));
                    },
                },
                Assignee: {
                    filterType: 'AsyncSelect',
                    filterId: 'filterUserId',
                    label: 'Filter by Assignees',
                    defaultMethod: FilterMethod.EQ,
                    isMulti: true,
                    loadOptions: async (searchTerm: string) => {
                        const users = rootStore.messageStore.providerUsers;
                        return users
                            .filter(user => {
                                const fullName = `${user.first || ''} ${user.last || ''}`.trim();
                                return !searchTerm || fullName.toLowerCase().includes(searchTerm.toLowerCase()) 
                                    || user.email?.toLowerCase().includes(searchTerm.toLowerCase());
                            })
                            .map(user => ({
                                label: `${user.first || ''} ${user.last || ''}`.trim() || user.email || 'Unknown User',
                                value: user.personalAccountId || '',
                            }));
                    },
                },
                filterRead: {
                    filterType: 'Custom',
                    filterId: 'filterRead',
                    label: 'Filter by Status',
                    component: StatusFilter,
                },
            };

            // Create a key that changes when categories or users are loaded
            const dataLoadedKey = `${rootStore.messageStore.messageCategories.length}-${rootStore.messageStore.providerUsers.length}`;
            
            return (
                <MessageListRedesigned
                    key={dataLoadedKey}
                    doneCount={doneTotal}
                    doneMessages={mapMessages(doneMessages)}
                    inboxCount={inboxTotal}
                    inboxMessages={mapMessages(inboxMessages)}
                    unreadCount={unreadInboxTotal}
                    onFetchData={handleFetchData}
                    onShowCompose={showCompose}
                    showFiltering={true}
                    showNavigation={true}
                    showSettings={true}
                    selectedMessageId={messageId}
                    customColumns={customColumns}
                    columnFiltersConfig={columnFiltersConfig}
                    rowActions={rowActions}
                />
            );
        };

        const renderComposePanel = (): React.ReactElement | undefined => {
            const state = window.history.state?.usr || {};
            const claimId = state.claimId;
            const subject = state.subject;
            const body = state.body;
            if (!isReadOnly) {
                return (
                    <S.ChatPanelContainer>
                        <ComposePanel claimId={claimId} subject={subject} body={body} hideNote={true} className="fixed" />
                    </S.ChatPanelContainer>
                );
            }
        };

        const paramsMessageId = params.messageId;
        const isComposePath = location.pathname.endsWith('/compose');

        const linkEntityToMessage = async (id: string, entityType: MessageLinkEntityType, entityIds: string[]): Promise<void> => {
            try {
                await CoreFetch(MessageApi).postProviderMessageLink({
                    id,
                    providerMessageLinkPayload: { entityType, entityIds }
                });
                
                setClaims([]);
                await refreshMessageDetail(id, true);
            } catch (error) {
                console.error('Failed to link entity:', error);
                throw error;
            }
        };

        const unlinkEntityFromMessage = async (id: string, entityType: MessageLinkEntityType, entityIds: string[]): Promise<void> => {
            try {
                await CoreFetch(MessageApi).postProviderMessageUnlink({
                    id,
                    providerMessageLinkPayload: { entityType, entityIds }
                });
                
                setClaims([]);
                await refreshMessageDetail(id, true);
            } catch (error) {
                console.error('Failed to unlink entity:', error);
                throw error;
            }
        };

        return (
            <MessagingProvider
                isReadOnlyClaimChoice={isReadOnlyClaimChoice}
                isReadOnly={isReadOnly}
                isListLoading={isListLoading}
                isDetailLoading={isDetailLoading}
                account={rootStore.account}
                enableMessageTypeValidation={true}
                message={messageDetail ? messageDetail.message : undefined}
                comments={messageDetail ? messageDetail.comments : undefined}
                documents={messageDetail ? messageDetail.documents : undefined}
                claims={claims}
                getCategoryName={rootStore.messageStore.getCategoryName}
                getUserName={rootStore.messageStore.getUserName}
                onClaimSearch={claimSearch}
                onComposeComment={composeComment}
                onComposeMessage={composeMessage}
                onDocumentDownload={downloadDocument}
                onDocumentActualDownload={actualDownloadDocument}
                onMarkAsClosed={markAsClosed}
                onMarkAsOpen={markAsOpen}
                onMarkAsRead={markAsRead}
                onMarkAsUnread={markAsUnread}
                onMessageClose={messageClose}
                onMessageOpen={messageOpen}
                onLinkEntityToMessage={linkEntityToMessage}
                onUnlinkEntityFromMessage={unlinkEntityFromMessage}>
                {renderMessageList()}
                {isComposePath && renderComposePanel()}
                {paramsMessageId && !isComposePath && (
                    <S.ChatPanelContainer>
                        <ChatPanelImpl onBack={() => navigate('..', { replace: true, relative: 'path' })} />
                    </S.ChatPanelContainer>
                )}
            </MessagingProvider>
        );
    }),
);

const ChatPanelImpl: React.FC<{ 
    onBack: () => void;
}> = ({ onBack }) => {
    return <ChatPanel hideNote={true} className="fixed" onBack={onBack} />;
};

